import { useState, useEffect, useCallback, useMemo } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { FolderSelection, FolderSelectionType } from '@/content-viewer/components/common/save-to-drive-modal/actions/schemas'
import { buildDocumentObjectForSaving, executeSaveArtifactToDrive } from '@/content-viewer/components/common/save-to-drive-modal/actions/save-artifact-to-drive'
import { createNewFolder } from '@/content-viewer/components/common/save-to-drive-modal/actions/create-new-folder'
import { useAppSelector } from '@/store/store-hooks'
import { selectRenderedMatterNameMap } from '@/matter/store/selectors'
import { store } from '@/store/store'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType } from '@/constants/constants-ui'
import { ArrowUpRight, NewFolderPlus } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { FolderPicker } from './components/FolderPicker'
import { CreateFolderDialog } from './components/CreateFolderDialog'
import { OverrideFileDialog } from './components/OverrideFileDialog'
import { Breadcrumbs, BreadcrumbItem } from './components/Breadcrumbs'
import { getStorage, ref, StorageReference } from 'firebase/storage'
import { kGcsUserFileUploadsBucketRef } from '@/constants/constants-gcs'
import { getAuth } from 'firebase/auth'
import { getBrandName } from '@/util/enterprise'
import { checkIfFilesExistInGCS } from '@/routes/dashboard/files/utils/files-utils'
import { getRelevantSegments } from './utils/folder-name-utils'

type SaveToDriveModalProps = {
  open: boolean
  onClose: () => void
  artifactVersionId: string | null
  defaultFilename: string | null
  matterId: string
}

enum ModalView {
  FORM = 'form',
  LOCATION_SELECTION = 'location-selection',
}

const DOCX_EXTENSION = '.docx'

const stripDocxExtension = (filename: string) => (filename.toLowerCase().endsWith(DOCX_EXTENSION) ? filename.slice(0, -DOCX_EXTENSION.length) : filename)

export function SaveToDriveModal({ open, onClose, artifactVersionId, defaultFilename, matterId }: SaveToDriveModalProps) {
  const [currentView, setCurrentView] = useState<ModalView>(ModalView.FORM)
  const [selectedFolder, setSelectedFolder] = useState<FolderSelection | null>(null)
  const [filename, setFilename] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false)
  const [showOverrideDialog, setShowOverrideDialog] = useState(false)
  const [creatingFolder, setCreatingFolder] = useState(false)

  const matterNameMap = useAppSelector(selectRenderedMatterNameMap)
  const matterName = matterNameMap.get(matterId) ?? matterId

  const storage = useMemo(() => getStorage(undefined, kGcsUserFileUploadsBucketRef), [])
  const auth = getAuth()
  const uid = auth.currentUser?.uid
  const userRootPath = uid ? `users/${uid}` : ''
  const matterPath = `${userRootPath}/${matterId}`

  const [currentFolderRef, setCurrentFolderRef] = useState<StorageReference | null>(null)
  const [stashedFolderRef, setStashedFolderRef] = useState<StorageReference | null>(null)
  const [stashedSelectedFolder, setStashedSelectedFolder] = useState<FolderSelection | null>(null)
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([])

  useEffect(() => {
    if (matterId) {
      setSelectedFolder({ type: FolderSelectionType.MATTER, matterId })
    }
  }, [matterId])

  useEffect(() => {
    if (defaultFilename) {
      setFilename(stripDocxExtension(defaultFilename))
    } else {
      setFilename('')
    }
  }, [defaultFilename])

  useEffect(() => {
    if (!open) {
      setCurrentView(ModalView.FORM)
    }
  }, [open])

  useEffect(() => {
    if (!selectedFolder || !userRootPath) {
      setCurrentFolderRef(null)
      return
    }

    let folderRef: StorageReference
    switch (selectedFolder.type) {
      case FolderSelectionType.MATTER:
        folderRef = ref(storage, `${userRootPath}/${selectedFolder.matterId}`)
        break
      case FolderSelectionType.CUSTOM:
        folderRef = selectedFolder.folderRef
        break
      default:
        folderRef = ref(storage, matterPath)
        break
    }

    setCurrentFolderRef(folderRef)
  }, [selectedFolder, storage, userRootPath, matterPath])

  const buildBreadcrumbs = useCallback(
    (folderRef: StorageReference): BreadcrumbItem[] => {
      const crumbs: BreadcrumbItem[] = []
      const relevantSegments = getRelevantSegments(folderRef.fullPath)

      const matterRef = ref(storage, matterPath)
      crumbs.push({ label: matterName, storageRef: matterRef })

      const subfoldersAfterMatter = relevantSegments.slice(1)
      let currentPath = matterPath
      for (const segment of subfoldersAfterMatter) {
        currentPath = `${currentPath}/${segment}`
        const segmentRef = ref(storage, currentPath)
        crumbs.push({ label: segment, storageRef: segmentRef })
      }

      return crumbs
    },
    [storage, matterPath, matterName]
  )

  useEffect(() => {
    if (currentFolderRef) {
      setBreadcrumbs(buildBreadcrumbs(currentFolderRef))
    } else {
      setBreadcrumbs([])
    }
  }, [currentFolderRef, buildBreadcrumbs])

  const deriveSelectionFromRef = useCallback((folderRef: StorageReference): FolderSelection => {
    const relevantSegments = getRelevantSegments(folderRef.fullPath)

    if (relevantSegments.length === 1) {
      return { type: FolderSelectionType.MATTER, matterId: relevantSegments[0] }
    }

    return { type: FolderSelectionType.CUSTOM, folderRef }
  }, [])

  const handleNavigate = useCallback(
    (folderRef: StorageReference) => {
      if (!folderRef.fullPath.startsWith(matterPath)) {
        return
      }
      setCurrentFolderRef(folderRef)
      setSelectedFolder(deriveSelectionFromRef(folderRef))
    },
    [deriveSelectionFromRef, matterPath]
  )

  const buildFilePath = (): string => {
    const uid = auth.currentUser?.uid
    if (!selectedFolder || !uid) return ''

    let folderPath = `users/${uid}/${matterId}`

    if (selectedFolder.type === FolderSelectionType.CUSTOM) {
      folderPath = selectedFolder.folderRef.fullPath
    }

    return `${folderPath}/${filename}.docx`
  }

  const handleSave = async () => {
    if (!artifactVersionId || !selectedFolder) return

    const filePath = buildFilePath()
    const fileExists = (await checkIfFilesExistInGCS([filePath])).length > 0

    if (fileExists) {
      setShowOverrideDialog(true)
      return
    }

    await performSave()
  }

  const folderDisplayPath = useMemo((): string => {
    if (!selectedFolder) return matterName

    if (selectedFolder.type !== FolderSelectionType.CUSTOM) {
      return matterName
    }

    const relevantSegments = getRelevantSegments(selectedFolder.folderRef.fullPath)
    const pathSegments = [matterName, ...relevantSegments.slice(1)]
    return pathSegments.join(' / ')
  }, [selectedFolder, matterName])

  const performSave = async () => {
    if (!artifactVersionId || !selectedFolder) return

    setUploading(true)
    try {
      const exportData = await buildDocumentObjectForSaving(artifactVersionId)

      await executeSaveArtifactToDrive(
        { ...exportData, defaultFilename: filename + DOCX_EXTENSION },
        {
          destinationFolder: selectedFolder,
          onProgress: (progress) => setUploadProgress(progress),
        }
      )

      store.dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: 'Document saved successfully',
        })
      )

      onClose()
    } catch {
      store.dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Failed to save document. Please try again.',
        })
      )
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleCreateNewFolder = async (folderName: string) => {
    if (!selectedFolder) return

    setCreatingFolder(true)
    try {
      const storage = getStorage(undefined, kGcsUserFileUploadsBucketRef)
      const uid = auth.currentUser?.uid

      let currentFolderPath = `users/${uid}/${matterId}`

      if (selectedFolder.type === FolderSelectionType.CUSTOM) {
        currentFolderPath = selectedFolder.folderRef.fullPath
      }

      await createNewFolder(currentFolderPath, folderName)

      const newFolderRef = ref(storage, `${currentFolderPath}/${folderName}`)
      setSelectedFolder({ type: FolderSelectionType.CUSTOM, folderRef: newFolderRef })

      store.dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: `Folder "${folderName}" created successfully`,
        })
      )

      setCreateFolderDialogOpen(false)
    } catch {
      store.dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Failed to create folder. Please try again.',
        })
      )
    } finally {
      setCreatingFolder(false)
    }
  }

  const canSave = !uploading && filename.trim()

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="bg-black/50 fixed inset-0 z-[999]" />
          <Dialog.Content
            className={`fixed top-1/2 left-1/2 w-[90vw] max-w-[640px] ${
              currentView === ModalView.LOCATION_SELECTION ? 'max-h-[517px] min-h-[517px]' : 'h-auto'
            } -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-lg focus:outline-none z-[999] overflow-hidden flex flex-col`}
            onEscapeKeyDown={(e) => {
              if (uploading) e.preventDefault()
            }}
            onInteractOutside={(e) => {
              if (uploading) e.preventDefault()
            }}
          >
            <Dialog.Title className="flex-shrink-0">
              <div className="text-left text-xl font-semibold px-6 pt-6 pb-2">Save copy to matter files</div>
            </Dialog.Title>

            {currentView === ModalView.LOCATION_SELECTION && (
              <Dialog.Description asChild>
                <div className="flex-shrink-0">
                  <p className="text-sm text-brand-neutral-600 px-6">Choose a folder within this matter to save your file.</p>

                  <div className="border-b border-brand-neutral-200 py-2.5 px-4">
                    <Breadcrumbs breadcrumbs={breadcrumbs} onBreadcrumbClick={handleNavigate} rootLabel={matterName} />
                  </div>
                </div>
              </Dialog.Description>
            )}

            <div className="flex-1 overflow-y-auto min-h-0">
              {currentView === ModalView.FORM && (
                <div className="px-6 pb-6 pt-0">
                  <Dialog.Description className="text-brand-neutral-950 text-base font-normal tracking-[-0.32px] leading-[24px] mb-6">
                    Saves a snapshot of your draft as a .docx file. This copy won't reflect any future changes you make in {getBrandName()}.
                  </Dialog.Description>
                  <div className="flex flex-col gap-4 mb-2">
                    <div className="relative">
                      <input
                        id="filename"
                        type="text"
                        className="block w-full h-14 px-3 pt-5 pb-2 text-base rounded border border-brand-neutral-300 bg-white focus:border-brand-500 focus:ring-0 focus:outline-none placeholder-transparent peer"
                        placeholder={filename}
                        value={filename}
                        onChange={(e) => setFilename(e.target.value)}
                        aria-describedby="file-name-extension-hint"
                        autoFocus
                      />
                      <label
                        htmlFor="filename"
                        className="absolute left-3 top-4 text-base text-brand-neutral-600 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:text-xs peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-xs"
                      >
                        File Name*
                      </label>
                    </div>
                    <div className="relative">
                      <div
                        id="folder-display-text"
                        className="block w-full h-14 px-3 pt-5 pb-2 text-base rounded border border-brand-neutral-300 bg-white overflow-hidden text-ellipsis whitespace-nowrap"
                        role="textbox"
                        aria-readonly="true"
                        aria-label="Selected folder location"
                      >
                        <span className="text-brand-neutral-950">{folderDisplayPath}</span>
                      </div>
                      <span className="absolute left-3 top-2 text-xs text-brand-neutral-600">Location*</span>
                    </div>
                  </div>

                  <Button
                    variant="plain"
                    size="sm"
                    trailingIcon={<ArrowUpRight className="w-5 h-5" aria-hidden="true" />}
                    onClick={() => {
                      setStashedFolderRef(currentFolderRef)
                      setStashedSelectedFolder(selectedFolder)
                      setCurrentView(ModalView.LOCATION_SELECTION)
                    }}
                    aria-label="Choose another folder location"
                  >
                    Choose Another Location
                  </Button>
                </div>
              )}
              {currentView === ModalView.LOCATION_SELECTION && (
                <div className="flex flex-col h-full">
                  <FolderPicker currentFolderRef={currentFolderRef} onNavigate={handleNavigate} renderedMatterNameMap={matterNameMap} />
                </div>
              )}
            </div>

            <div className="flex-shrink-0 py-4 px-6 w-full border-t border-b border-brand-neutral-200">
              <div className="flex flex-col md:flex-row justify-between gap-3 items-stretch md:items-center md:justify-between">
                {currentView === ModalView.FORM && (
                  <>
                    <p className="text-sm text-brand-neutral-600 flex-shrink-0 text-left">
                      <span className="text-red-700">*</span> Required fields
                    </p>
                    <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                      <Dialog.Close asChild>
                        <Button variant="outlined" disabled={uploading} fullWidth>
                          Cancel
                        </Button>
                      </Dialog.Close>
                      <Button onClick={handleSave} disabled={!canSave} loading={uploading || uploadProgress > 0} fullWidth>
                        {uploading || uploadProgress > 0 ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </>
                )}
                {currentView === ModalView.LOCATION_SELECTION && (
                  <div className="flex items-center justify-between w-full">
                    <Button
                      variant="plain"
                      size="sm"
                      leadingIcon={<NewFolderPlus className="w-4 h-4" aria-hidden="true" />}
                      onClick={() => setCreateFolderDialogOpen(true)}
                      aria-label="Create new folder in current location"
                    >
                      New Folder
                    </Button>
                    <div className="flex gap-3">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          if (stashedFolderRef) {
                            setCurrentFolderRef(stashedFolderRef)
                            setStashedFolderRef(null)
                          }
                          if (stashedSelectedFolder) {
                            setSelectedFolder(stashedSelectedFolder)
                            setStashedSelectedFolder(null)
                          }
                          setCurrentView(ModalView.FORM)
                        }}
                        aria-label="Cancel location selection and revert to previous folder"
                      >
                        Cancel
                      </Button>
                      <Button disabled={uploading} onClick={() => setCurrentView(ModalView.FORM)} aria-label="Confirm folder selection and return to save form">
                        Done
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <CreateFolderDialog
        open={createFolderDialogOpen}
        onSave={handleCreateNewFolder}
        onClose={() => setCreateFolderDialogOpen(false)}
        isCreating={creatingFolder}
      />

      <OverrideFileDialog
        open={showOverrideDialog}
        fileName={`${filename}${DOCX_EXTENSION}`}
        onOverride={async () => {
          setShowOverrideDialog(false)
          await performSave()
        }}
        onRename={() => setShowOverrideDialog(false)}
        onClose={() => setShowOverrideDialog(false)}
      />
    </>
  )
}
